import React from "react";
import type { IServiceEmployee } from "@/services/types";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { formatEmployeeName } from "@/hooks/data/dataQuality";
import styles from "./EmployeeSettingsModal.module.css";
import { generateId } from "@/lib/utils";

export interface EmployeeSettingsModalProps {
  employee: IServiceEmployee;
  isUpdating?: boolean;
  onToggleBookable: (checked: boolean) => void;
  onClose: () => void;
  testId?: string;
}

export const EmployeeSettingsModal: React.FC<EmployeeSettingsModalProps> = ({
  employee,
  isUpdating = false,
  onToggleBookable,
  onClose,
  testId = "employee-settings-modal",
}) => {
  const displayName = formatEmployeeName(employee);

  const checkboxId = testId
    ? `${testId}-bookable-checkbox-${employee.id}`
    : generateId("employee-settings-bookable", employee.id);

  // Local state for immediate UI feedback
  const [bookable, setBookable] = React.useState(
    employee.settings?.is_bookable !== false,
  );

  // Keep in sync if employee changes or server data refreshes
  React.useEffect(() => {
    setBookable(employee.settings?.is_bookable !== false);
  }, [employee.id, employee.settings?.is_bookable]);

  const handleChange = (checked: boolean) => {
    setBookable(checked); // optimistic update
    onToggleBookable(checked); // trigger server update
  };

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      data-testid={`${testId}-overlay`}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${testId}-title`}
        data-testid={testId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={styles.header}>
          <h2 id={`${testId}-title`} className={styles.title}>
            Innstillinger for {displayName}
          </h2>

          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Lukk"
            data-testid={`${testId}-close`}
          >
            ✕
          </button>
        </header>

        <div className={styles.content}>
          <div className={styles.settingRow}>
            <div className={styles.settingInfo}>
              <span className={styles.settingLabel}>Bookbar</span>
              <span className={styles.settingDescription}>
                Mulig å booke ansatte
              </span>
            </div>

            <div onClick={(e) => e.stopPropagation()}>
              <Checkbox
                id={checkboxId}
                checked={bookable} // use local state
                label="Bookbar"
                onChange={handleChange} // optimistic handler
                disabled={isUpdating}
                aria-label={`Sett ${displayName} bookbar`}
                data-testid={`${testId}-bookable-checkbox`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

EmployeeSettingsModal.displayName = "EmployeeSettingsModal";
export default EmployeeSettingsModal;
